import { useEffect, useState } from 'react';
import {
  connectGmail,
  disconnectGmail,
  getClients,
  getEmailSenderReadiness,
  getEmailSettings,
  getGmailConnectionStatus,
  getSubscription,
  saveEmailSettings,
  sendMonthlyReport,
  sendTestEmail,
} from '../utils/api';

const ENABLE_EMAIL_SCHEDULER = process.env.REACT_APP_ENABLE_EMAIL_SCHEDULER === 'true';

const TIMEZONE_OPTIONS = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Berlin',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
];

const PLATFORM_OPTIONS = [
  { value: '', label: 'All platforms' },
  { value: 'meta', label: 'Meta Ads' },
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'linkedin_ads', label: 'LinkedIn Ads' },
  { value: 'shopify', label: 'Shopify' },
];

const defaultForm = {
  enabled: false,
  recipient_email: '',
  cc_email: '',
  send_day: 1,
  timezone: 'UTC',
  preferred_send_hour: 9,
  subject_template: '',
  message_template: '',
  platform_filter: '',
  report_title_template: '',
};

// UI-only gating for UX — the server independently re-checks the plan and
// returns a structured 403 (code: 'feature_not_available') on every route
// behind this page regardless of what this component shows.
export default function EmailScheduling() {
  const [plan, setPlan] = useState(undefined);

  useEffect(() => {
    if (!ENABLE_EMAIL_SCHEDULER) return;
    getSubscription()
      .then((sub) => setPlan(sub?.plan_name || 'free'))
      .catch(() => setPlan('free'));
  }, []);

  if (!ENABLE_EMAIL_SCHEDULER) {
    return (
      <div>
        <h1>Email Scheduling</h1>
        <p>Automatic monthly report emails are coming soon. Manual report generation and download remain available.</p>
      </div>
    );
  }

  if (plan === undefined) return null;

  if (plan === 'free') {
    return (
      <div className="card card-pad" style={{ maxWidth: 560 }}>
        <h1 style={{ fontSize: 20, marginTop: 0 }}>Email Scheduling is a Pro/Agency feature</h1>
        <p style={{ color: 'var(--text2, #64748b)' }}>
          Automatically emailing each client their monthly PDF report requires the Pro or Agency plan. You can still
          generate and download reports manually on every plan.
        </p>
        <a href="/subscription" className="btn btn-primary" style={{ textDecoration: 'none', display: 'inline-block', marginTop: 8 }}>
          View plans
        </a>
      </div>
    );
  }

  return <EmailSchedulingEnabled plan={plan} />;
}

function EmailSchedulingEnabled({ plan }) {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [settings, setSettings] = useState(defaultForm);
  const [readiness, setReadiness] = useState(null);
  const [gmailStatus, setGmailStatus] = useState(null);
  const [loading, setLoading] = useState('');
  const [message, setMessage] = useState('');
  const [messageIsError, setMessageIsError] = useState(false);

  useEffect(() => {
    getClients().then((rows) => {
      setClients(rows || []);
      if (rows?.[0]?.id) setSelectedClient(rows[0].id);
    }).catch(() => setClients([]));

    getEmailSenderReadiness().then(setReadiness).catch(() => setReadiness(null));
    getGmailConnectionStatus().then(setGmailStatus).catch(() => setGmailStatus(null));
  }, []);

  useEffect(() => {
    if (!selectedClient) return;
    getEmailSettings(selectedClient)
      .then((data) => setSettings({ ...defaultForm, ...data }))
      .catch(() => setSettings(defaultForm));
  }, [selectedClient]);

  const showMessage = (text, isError = false) => {
    setMessage(text);
    setMessageIsError(isError);
  };

  const hasSender = readiness?.hasAnySender;

  const handleGmailConnect = async () => {
    try {
      setLoading('gmail-connect');
      const result = await connectGmail({ clientId: selectedClient });
      if (result.url) window.location.href = result.url;
    } catch (err) {
      showMessage(err.response?.data?.error || 'Failed to start Gmail connection.', true);
    } finally {
      setLoading('');
    }
  };

  const handleGmailDisconnect = async () => {
    if (!window.confirm('Disconnect Gmail? Automatic sends will fall back to SMTP if configured, or be blocked otherwise.')) return;
    try {
      setLoading('gmail-disconnect');
      await disconnectGmail();
      setGmailStatus(await getGmailConnectionStatus());
      setReadiness(await getEmailSenderReadiness());
      showMessage('Gmail disconnected.');
    } catch (err) {
      showMessage(err.response?.data?.error || 'Failed to disconnect Gmail.', true);
    } finally {
      setLoading('');
    }
  };

  const handleFieldChange = (field, value) => setSettings((prev) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    // Confirmation before enabling — not required when only editing an
    // already-enabled schedule or disabling one.
    if (settings.enabled && !hasSender) {
      showMessage('Connect Gmail or configure SMTP before enabling automatic scheduling.', true);
      return;
    }
    if (settings.enabled) {
      const confirmed = window.confirm(
        `Enable automatic monthly emails for this client?\n\nA report will be generated and sent to ${settings.recipient_email || '(no recipient set)'} on day ${settings.send_day} of each month at ${String(settings.preferred_send_hour).padStart(2, '0')}:00 (${settings.timezone}).`
      );
      if (!confirmed) return;
    }

    try {
      setLoading('save');
      const saved = await saveEmailSettings(selectedClient, settings);
      setSettings({ ...defaultForm, ...saved });
      showMessage('Schedule saved.');
    } catch (err) {
      showMessage(err.response?.data?.error || 'Failed to save schedule.', true);
    } finally {
      setLoading('');
    }
  };

  const handleSendTest = async () => {
    if (!settings.recipient_email) {
      showMessage('Enter a recipient email first.', true);
      return;
    }
    try {
      setLoading('test');
      await sendTestEmail({ recipient_email: settings.recipient_email });
      showMessage('Test email sent (no report attached, no schedule affected).');
    } catch (err) {
      showMessage(err.response?.data?.error || 'Failed to send test email.', true);
    } finally {
      setLoading('');
    }
  };

  const handleSendNow = async () => {
    if (!window.confirm('Send the most recent complete month\'s report to this client right now?')) return;
    try {
      setLoading('send-now');
      const result = await sendMonthlyReport(selectedClient);
      setSettings((prev) => ({ ...prev, last_send_status: 'sent', last_error: null }));
      showMessage(`Sent report for ${result.reportMonth || 'the most recent month'}.`);
    } catch (err) {
      const data = err.response?.data || {};
      showMessage(data.error || 'Failed to send report now.', true);
    } finally {
      setLoading('');
    }
  };

  const formatDate = (value) => (value ? new Date(value).toLocaleString() : 'Not sent yet');
  const busy = Boolean(loading);

  return (
    <div>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', margin: 0 }}>Email Scheduling</h1>
        <p style={{ color: '#64748b', marginTop: 6, fontSize: 13 }}>
          Automatically email each client their completed previous-month PDF report, generated by the same report
          engine used for manual downloads.
        </p>
      </div>

      {plan === 'agency' && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 14, padding: '14px 18px', marginBottom: 18, fontSize: 13, color: '#1d4ed8' }}>
          Agency plan: automatic scheduling is available for all your clients, subject to a reasonable monthly sending limit.
        </div>
      )}
      {plan === 'pro' && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 14, padding: '14px 18px', marginBottom: 18, fontSize: 13, color: '#92400e' }}>
          Pro plan: manual/test sending and automatic scheduling are both available, sharing your plan's monthly report-generation limit.
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 18, marginBottom: 18 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 8 }}>Client</label>
        <select
          value={selectedClient}
          onChange={(e) => setSelectedClient(e.target.value)}
          style={{ width: '100%', maxWidth: 420, padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 10 }}
        >
          <option value="">Select Client</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        {message && (
          <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: messageIsError ? '#fef2f2' : '#eff6ff', color: messageIsError ? '#dc2626' : '#1d4ed8', fontSize: 13, fontWeight: 600 }}>
            {message}
          </div>
        )}
      </div>

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 18, marginBottom: 18 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 16 }}>Sender</h2>
        {readiness === null ? (
          <div style={{ fontSize: 13, color: '#64748b' }}>Loading sender status...</div>
        ) : !readiness.googleOAuthConfigured && !readiness.smtpConfigured ? (
          <div style={{ padding: 12, borderRadius: 10, background: '#f1f5f9', color: '#64748b', fontSize: 13 }}>
            Configuration required — no email sender is set up on this server yet. Contact your administrator.
          </div>
        ) : (
          <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.9 }}>
            <div>
              <strong>Your Gmail:</strong>{' '}
              {gmailStatus?.connected ? `Connected (${gmailStatus.email})` : 'Not connected'}
            </div>
            <div><strong>Agency SMTP fallback:</strong> {readiness.smtpConfigured ? 'Configured' : 'Not configured'}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              {readiness.googleOAuthConfigured && !gmailStatus?.connected && (
                <button onClick={handleGmailConnect} disabled={busy} className="btn-inline" style={btnPrimary}>
                  Connect Gmail
                </button>
              )}
              {gmailStatus?.connected && (
                <button onClick={handleGmailDisconnect} disabled={busy} style={btnDanger}>
                  Disconnect Gmail
                </button>
              )}
            </div>
            {!hasSender && (
              <div style={{ marginTop: 8, color: '#b45309' }}>
                Connect Gmail or ask your administrator to configure SMTP before enabling automatic scheduling.
              </div>
            )}
          </div>
        )}
      </div>

      {selectedClient && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 18 }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 16 }}>Schedule</h2>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
            <input
              type="checkbox"
              id="enabled"
              checked={settings.enabled}
              onChange={(e) => handleFieldChange('enabled', e.target.checked)}
              disabled={busy}
            />
            <label htmlFor="enabled" style={{ fontSize: 13, fontWeight: 700 }}>Automatic monthly sending enabled</label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Recipient email</label>
              <input type="email" value={settings.recipient_email} onChange={(e) => handleFieldChange('recipient_email', e.target.value)} style={inputStyle} disabled={busy} />
            </div>
            <div>
              <label style={labelStyle}>CC (comma-separated, up to 5)</label>
              <input type="text" value={settings.cc_email} onChange={(e) => handleFieldChange('cc_email', e.target.value)} style={inputStyle} disabled={busy} />
            </div>
            <div>
              <label style={labelStyle}>Send day of month</label>
              <select value={settings.send_day} onChange={(e) => handleFieldChange('send_day', Number(e.target.value))} style={inputStyle} disabled={busy}>
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Send hour (local)</label>
              <select value={settings.preferred_send_hour} onChange={(e) => handleFieldChange('preferred_send_hour', Number(e.target.value))} style={inputStyle} disabled={busy}>
                {Array.from({ length: 24 }, (_, h) => h).map((h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Timezone</label>
              <select value={settings.timezone} onChange={(e) => handleFieldChange('timezone', e.target.value)} style={inputStyle} disabled={busy}>
                {TIMEZONE_OPTIONS.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Platform filter</label>
              <select value={settings.platform_filter} onChange={(e) => handleFieldChange('platform_filter', e.target.value)} style={inputStyle} disabled={busy}>
                {PLATFORM_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Subject (optional — use {'{client}'} and {'{month}'})</label>
            <input type="text" value={settings.subject_template} onChange={(e) => handleFieldChange('subject_template', e.target.value)} style={inputStyle} disabled={busy} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={labelStyle}>Short message (optional)</label>
            <textarea value={settings.message_template} onChange={(e) => handleFieldChange('message_template', e.target.value)} style={{ ...inputStyle, minHeight: 70 }} disabled={busy} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Report title (optional)</label>
            <input type="text" value={settings.report_title_template} onChange={(e) => handleFieldChange('report_title_template', e.target.value)} style={inputStyle} disabled={busy} />
          </div>

          <div style={{ background: '#f8fafc', borderRadius: 12, padding: 12, fontSize: 12, color: '#475569', lineHeight: 1.8, marginBottom: 16 }}>
            <div><strong>Last sent:</strong> {formatDate(settings.last_sent_at)}</div>
            <div><strong>Next scheduled send:</strong> {settings.enabled ? formatDate(settings.next_send_at) : 'Not scheduled (disabled)'}</div>
            <div><strong>Last status:</strong> {settings.last_send_status || '—'}</div>
            {settings.last_error && <div style={{ color: '#dc2626' }}><strong>Last error:</strong> {settings.last_error}</div>}
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={handleSave} disabled={busy} style={btnPrimary}>{loading === 'save' ? 'Saving...' : 'Save Schedule'}</button>
            <button onClick={handleSendTest} disabled={busy} style={btnSecondary}>{loading === 'test' ? 'Sending...' : 'Send Test'}</button>
            <button onClick={handleSendNow} disabled={busy} style={btnSecondary}>{loading === 'send-now' ? 'Sending...' : 'Send Now'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, color: '#334155', marginBottom: 4 };
const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 };
const btnPrimary = { border: 0, borderRadius: 10, background: '#2563eb', color: '#fff', padding: '10px 16px', fontWeight: 800, cursor: 'pointer' };
const btnSecondary = { border: '1px solid #cbd5e1', borderRadius: 10, background: '#fff', color: '#334155', padding: '10px 16px', fontWeight: 800, cursor: 'pointer' };
const btnDanger = { border: '1px solid #fecaca', borderRadius: 10, background: '#fff', color: '#dc2626', padding: '10px 16px', fontWeight: 800, cursor: 'pointer' };
